import { Box, Button, Dialog, DialogContent, DialogTitle, Grid, MenuItem, Typography } from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import FormInput from "components/forminput";
import AOESelect from "components/aoeselect";
import LanguageSelect from "components/languageslect";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { useMutation } from "@tanstack/react-query";
import { string, array, object, boolean } from "yup";
import { Formik } from "formik";
import { useAuth } from "hooks/useUser";

const validationSchema = object({
  education: string().required("Required"),
  fieldId: string().required("Required"),
  employmentStatus: string().required("Required"),
  maritalStatus: string().required("Required"),
  otherLanguages: array().of(string()).required("Required"),
  primaryLanguageId: string().required("Required"),
  completedProfile: boolean().required("Required"),
});

async function updateInfo(input: ReturnType<typeof validationSchema.cast>) {
  const { data } = await axios.patch("/auth/agent/profile", input);
  return data;
}

const employmentOptions = [
  { key: "FullTime", value: "Full Time" },
  { key: "PartTime", value: "Part Time" },
  { key: "Unemployed", value: "Unemployed" },
  { key: "SelfEmployed", value: "Self Employed" },
  { key: "HomeMaker", value: "Home Maker" },
  { key: "Student", value: "Student" },
  { key: "Retired", value: "Retired" },
];

const educationOptions = [
  { key: "Less_secondary_school_degree", value: "Less than secondary school degree" },
  { key: "High_school_or_secondary_school", value: "High school/Secondary school" },
  { key: "Trade_Technical_School", value: "Trade Technical School" },
  { key: "Bachelors_degree", value: "Bachelor's degree" },
  { key: "Masters_degree_or_diploma", value: "Master's degree or diploma" },
  { key: "Doctorate", value: "Doctorate" },
  { key: "Other", value: "Others" },
];

const maritalStatus = [
  { key: "Married", value: "Married" },
  { key: "Divorced", value: "Divorced" },
  { key: "Separated", value: "Separated" },
  { key: "Widowed", value: "Widowed" },
  { key: "Single", value: "Single" },
  { key: "Prefer_not_to_say", value: "Prefer not to say" },
];

function Setup2() {
  const navigate = useNavigate();
  const { data: user } = useAuth();
  const update = useMutation(updateInfo, {
    onSuccess() {
      navigate("/dashboard/home");
    },
  });
  return (
    <Dialog open maxWidth="md" fullWidth>
      <DialogTitle
        sx={{
          display: "grid",
          gridTemplateColumns: "auto 1fr auto",
        }}>
        <Button
          variant="text"
          onClick={() => {
            navigate(-1);
          }}
          startIcon={<ArrowBackIcon />}
        />
        <Typography variant="h6" align="center" component="p">
          Account Setup
        </Typography>
        <Typography alignSelf="center" color="primary">
          2/3
        </Typography>
      </DialogTitle>
      <DialogContent>
        <Formik
          initialValues={{
            education: user.education || "",
            fieldId: user.fieldId || "",
            maritalStatus: user.maritalStatus || "",
            otherLanguages: user.otherLanguages?.map(({ id }) => id) || [],
            primaryLanguageId: user.primaryLanguageId || "",
            completedProfile: true,
            employmentStatus: user.employmentStatus || "",
          }}
          validationSchema={validationSchema}
          onSubmit={(data) => {
            update.mutate(data);
          }}>
          {({ handleBlur, handleSubmit, handleChange, setFieldValue, touched, values, errors }) => (
            <Box component="form" onSubmit={handleSubmit}>
              <Grid container rowSpacing={2} columnSpacing={4}>
                <Grid item xs={12}>
                  <FormInput
                    onChange={handleChange}
                    onBlur={handleBlur}
                    value={values.education}
                    error={touched.education && !!errors.education}
                    helperText={touched.education && errors.education}
                    id="education"
                    label="Educational Background"
                    name="education"
                    select>
                    {educationOptions.map((option) => (
                      <MenuItem key={option.key} value={option.key}>
                        {option.value}
                      </MenuItem>
                    ))}
                  </FormInput>
                </Grid>
                <Grid item xs={12}>
                  <AOESelect
                    id="fieldId"
                    label="Area of Expertise"
                    name="fieldId"
                    onChange={(value) => {
                      setFieldValue("fieldId", value);
                    }}
                    value={values.fieldId}
                    // error={touched.fieldId && !!errors.fieldId}
                    // helperText={touched.fieldId && errors.fieldId}
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <FormInput
                    onChange={handleChange}
                    onBlur={handleBlur}
                    value={values.maritalStatus}
                    error={touched.maritalStatus && !!errors.maritalStatus}
                    helperText={touched.maritalStatus && errors.maritalStatus}
                    id="maritalStatus"
                    label="Marital Status"
                    name="maritalStatus"
                    select>
                    {maritalStatus.map((option) => (
                      <MenuItem key={option.key} value={option.key}>
                        {option.value}
                      </MenuItem>
                    ))}
                  </FormInput>
                </Grid>
                <Grid item xs={12} md={6}>
                  <FormInput
                    onChange={handleChange}
                    onBlur={handleBlur}
                    value={values.employmentStatus}
                    error={touched.employmentStatus && !!errors.employmentStatus}
                    helperText={touched.employmentStatus && errors.employmentStatus}
                    id="employmentStatus"
                    label="Employment Status"
                    name="employmentStatus"
                    select>
                    {employmentOptions.map((option) => (
                      <MenuItem key={option.key} value={option.key}>
                        {option.value}
                      </MenuItem>
                    ))}
                  </FormInput>
                </Grid>
                <Grid item xs={12} md={6}>
                  <LanguageSelect
                    id="primaryLanguageId"
                    label="Primary Language"
                    name="primaryLanguageId"
                    onChange={handleChange}
                    value={values.primaryLanguageId}
                    error={touched.primaryLanguageId && !!errors.primaryLanguageId}
                    helperText={touched.primaryLanguageId && errors.primaryLanguageId}
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <LanguageSelect
                    id="otherLanguages"
                    label="Spoken Languages"
                    name="otherLanguages"
                    value={values.otherLanguages}
                    onChange={handleChange}
                    error={touched.otherLanguages && !!errors.otherLanguages}
                    helperText={touched.otherLanguages && errors.otherLanguages}
                    SelectProps={{
                      multiple: true,
                    }}
                  />
                </Grid>
              </Grid>
              <Button
                disabled={update.isLoading}
                type="submit"
                fullWidth
                variant="contained"
                size="large"
                sx={{ mt: 4 }}>
                Continue
              </Button>
              <Button
                onClick={() => {
                  navigate("/dashboard/home");
                }}
                fullWidth
                variant="text"
                size="large"
                sx={{ mb: 4 }}>
                I'll do this later
              </Button>
            </Box>
          )}
        </Formik>
      </DialogContent>
    </Dialog>
  );
}

export default Setup2;
